import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  Image,
  X,
  CheckCircle,
  Loader2,
  Camera,
  FileText,
  AlertTriangle,
  MessageSquare,
  Shield,
  Info
} from 'lucide-react';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/ui/PageHeader';
import { useApp } from '../../context/AppContext';
import { useBroker } from '../../context/BrokerContext';
import { processReceiptImage } from '../../services/ocrService';
import { verifyReceipt } from '../../services/receiptVerificationService';
import { whatsappAPI, penaltyService } from '../../services/api';
import { documentNotificationService } from '../../services/emailService';
import './UploadReceipt.css';

// Helper function to convert File to base64 with timeout and abort handling
const fileToBase64 = (file, timeout = 30000) => {
  return new Promise((resolve, reject) => {
    // Validate file input
    if (!file || !(file instanceof Blob)) {
      reject(new Error('Invalid file provided'));
      return;
    }

    const reader = new FileReader();
    let timeoutId = null;

    // Cleanup function
    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    // Set timeout to prevent hanging
    timeoutId = setTimeout(() => {
      cleanup();
      reader.abort();
      reject(new Error(`File conversion timed out after ${timeout/1000} seconds`));
    }, timeout);

    reader.onload = () => {
      cleanup();
      if (reader.result) {
        resolve(reader.result);
      } else {
        reject(new Error('FileReader returned empty result'));
      }
    };

    reader.onerror = (error) => {
      cleanup();
      reject(new Error(`FileReader error: ${error?.message || 'Unknown error'}`));
    };

    reader.onabort = () => {
      cleanup();
      reject(new Error('File reading was aborted'));
    };

    try {
      reader.readAsDataURL(file);
    } catch (err) {
      cleanup();
      reject(new Error(`Failed to start reading file: ${err.message}`));
    }
  });
};

// Retry wrapper for fileToBase64 with logging
const fileToBase64WithRetry = async (file, maxRetries = 3) => {
  let lastError = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`[Base64] Converting file attempt ${attempt}/${maxRetries}: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
      const result = await fileToBase64(file);
      console.log(`[Base64] Conversion successful on attempt ${attempt}`);
      return result;
    } catch (err) {
      lastError = err;
      console.error(`[Base64] Attempt ${attempt} failed:`, err.message);

      if (attempt < maxRetries) {
        // Wait before retry (exponential backoff)
        const delay = attempt * 500;
        console.log(`[Base64] Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
};

function UploadReceipt() {
  const navigate = useNavigate();
  const { currentTransaction, updateTransaction, setCurrentTransaction, getActiveAIConfig } = useApp();
  const { getBrokerPath } = useBroker();
  const fileInputRef = useRef(null);
  const activeAIConfig = getActiveAIConfig();

  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadState, setUploadState] = useState('idle'); // idle, uploading, processing, success, mismatch, error, manual_review
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [error, setError] = useState(null);
  const [mismatchInfo, setMismatchInfo] = useState(null);
  const [attachmentWarning, setAttachmentWarning] = useState(false);

  // Redirect if no transaction
  useEffect(() => {
    if (!currentTransaction || !currentTransaction.referenceNumber) {
      navigate(getBrokerPath('/'));
    }
  }, [currentTransaction, navigate, getBrokerPath]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      setSelectedFile(file);
      setError(null);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      const event = { target: { files: [file] } };
      handleFileSelect(event);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setPreview(null);
    setOcrResult(null);
    setUploadState('idle');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploadState('uploading');
    setError(null);
    setOcrProgress(0);

    try {
      // Convert file to base64 for email attachment with retry
      let receiptBase64 = null;
      setAttachmentWarning(false);
      try {
        receiptBase64 = await fileToBase64WithRetry(selectedFile, 3);
      } catch (err) {
        console.error('[UploadReceipt] Failed to convert receipt to base64 after retries:', err);
        setAttachmentWarning(true);
        // Continue with upload - attachment will be missing but transaction can still be processed
      }

      // Simulate upload delay
      await new Promise(resolve => setTimeout(resolve, 500));

      setUploadState('processing');

      // Real OCR Processing with Tesseract.js
      const result = await processReceiptImage(selectedFile, (progress) => {
        setOcrProgress(progress);
      });

      if (result.success) {
        // Use real OCR extracted data
        const ocrData = {
          senderName: result.data.senderName || null,
          senderAccount: result.data.senderAccount || null,
          receiverName: result.data.receiverName || null,
          amount: result.data.amount || null,
          referenceNumber: result.data.referenceNumber || null,
          transactionId: result.data.transactionId || null,
          date: result.data.date || null,
          bankName: result.data.bankName || null,
          confidence: result.confidence,
          rawText: result.data.rawText,
        };

        setOcrResult(ocrData);

        // Check if OCR failed to extract key data
        const senderNameExtracted = ocrData.senderName && ocrData.senderName.trim().length > 0;
        const referenceExtracted = ocrData.referenceNumber && ocrData.referenceNumber.trim().length > 0;

        // If OCR fails to extract key details, escalate to manual review
        if (!senderNameExtracted || !referenceExtracted) {
          // Escalate to manual review
          updateTransaction({
            id: currentTransaction.id,
            receiptUploaded: true,
            ocrData,
            status: 'manual_review',
            verificationStatus: 'ocr_failed',
            ocrFailed: true,
            uploadedAt: new Date().toISOString(),
            // Include receipt for email attachment
            receiptBase64: receiptBase64,
            receiptName: selectedFile.name,
            receiptType: selectedFile.type,
            // Bank Transaction ID extracted from receipt (may still be available)
            bankTransactionId: ocrData.transactionId || null,
          });

          setCurrentTransaction({
            ...currentTransaction,
            receiptUploaded: true,
            ocrData,
            status: 'manual_review',
            verificationStatus: 'ocr_failed',
            // Include receipt for email attachment
            receiptBase64: receiptBase64,
            receiptName: selectedFile.name,
            receiptType: selectedFile.type,
            // Bank Transaction ID extracted from receipt (may still be available)
            bankTransactionId: ocrData.transactionId || null,
          });

          setUploadState('manual_review');

          // Send email notification directly (no OTP needed for receipt)
          if (currentTransaction.userEmail) {
            documentNotificationService.sendReceiptNotification({
              email: currentTransaction.userEmail,
              userName: currentTransaction.userName || currentTransaction.bankAccountTitle,
              transactionId: currentTransaction.id,
              referenceNumber: currentTransaction.referenceNumber,
              amount: currentTransaction.amount,
              bankName: currentTransaction.bankName,
              accountTitle: currentTransaction.bankAccountTitle,
              status: 'manual_review',
              ocrResult: ocrData,
              documentBase64: receiptBase64,
              documentName: selectedFile.name,
              documentType: selectedFile.type,
            });
          }

          // Navigate directly to transaction status page
          setTimeout(() => {
            navigate(`/status/${currentTransaction.id}`);
          }, 2000);
          return;
        }

        // Verify receipt data matches transaction
        const kycName = currentTransaction.bankAccountTitle?.toUpperCase().trim() || '';
        const ocrName = ocrData.senderName?.toUpperCase().trim() || '';

        // Check name match
        const kycWords = kycName.split(' ').filter(word => word.length > 0);
        const ocrWords = ocrName.split(' ').filter(word => word.length > 0);
        const allKycWordsFound = kycWords.every(kycWord =>
          ocrWords.some(ocrWord => ocrWord === kycWord)
        );
        const nameMatch = allKycWordsFound && kycWords.length > 0;

        // Check amount match
        const expectedAmount = currentTransaction.amount || 0;
        const ocrAmount = ocrData.amount || 0;
        const amountMatch = expectedAmount === ocrAmount;

        // Check reference match
        const refMatch = ocrData.referenceNumber &&
          currentTransaction.referenceNumber &&
          ocrData.referenceNumber.includes(currentTransaction.referenceNumber);

        // Collect issues
        const issues = [];
        const issueFields = [];

        if (!nameMatch) {
          issueFields.push('Account Name');
        }
        if (!amountMatch) {
          issueFields.push('Amount');
        }
        if (!refMatch) {
          issueFields.push('Reference Number');
        }

        if (issueFields.length > 0) {
          issues.push({
            type: 'verification_failed',
            message: `Discrepancies found in: ${issueFields.join(', ')}.`,
            fields: issueFields
          });
        }

        const hasMismatch = issues.length > 0;
        const verificationStatus = hasMismatch ? 'mismatched' : 'matched';

        // Calculate penalty if mismatch
        let penaltyDetails = null;
        if (hasMismatch) {
          const mismatchType = issueFields.length > 1 ? 'multiple_mismatch' :
            issueFields.includes('Account Name') ? 'name_mismatch' :
            issueFields.includes('Amount') ? 'amount_mismatch' : 'reference_mismatch';
          penaltyDetails = penaltyService.calculatePenalty(currentTransaction.amount, mismatchType);
        }

        // Run AI verification automatically when receipt is uploaded
        let aiVerificationResult = null;

        // Get the active AI config - try from context first, then localStorage fallback
        let aiConfig = activeAIConfig;
        if (!aiConfig) {
          console.log('[UploadReceipt] AI config not in context, checking localStorage...');
          try {
            const savedConfig = localStorage.getItem('ai_active_config_full');
            if (savedConfig) {
              aiConfig = JSON.parse(savedConfig);
              console.log('[UploadReceipt] Found AI config in localStorage:', aiConfig?.providerId);
            }
          } catch (e) {
            console.error('[UploadReceipt] Error reading AI config from localStorage:', e);
          }
        }

        if (aiConfig) {
          console.log('[UploadReceipt] Running AI verification automatically with provider:', aiConfig.providerId);

          // Retry mechanism for AI verification
          const maxRetries = 2;
          for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
              const caseData = {
                expectedAmount: currentTransaction.amount,
                senderAccount: currentTransaction.bankAccountNumber,
                expectedAccountTitle: currentTransaction.bankAccountTitle || currentTransaction.userName,
                expectedBank: currentTransaction.bankName
              };
              const aiResult = await verifyReceipt(aiConfig, receiptBase64, caseData);
              if (aiResult.success) {
                aiVerificationResult = aiResult.result;
                console.log('[UploadReceipt] AI verification completed:', aiResult.result.overall_status);
                break; // Success, exit retry loop
              } else {
                console.error(`[UploadReceipt] AI verification failed (attempt ${attempt}):`, aiResult.error);
                if (attempt < maxRetries) {
                  console.log('[UploadReceipt] Retrying in 2 seconds...');
                  await new Promise(resolve => setTimeout(resolve, 2000));
                }
              }
            } catch (aiError) {
              console.error(`[UploadReceipt] AI verification error (attempt ${attempt}):`, aiError);
              if (attempt < maxRetries) {
                console.log('[UploadReceipt] Retrying in 2 seconds...');
                await new Promise(resolve => setTimeout(resolve, 2000));
              }
            }
          }
        } else {
          console.log('[UploadReceipt] No AI config available - skipping auto verification');
        }

        // Update transaction with AI verification result
        updateTransaction({
          id: currentTransaction.id,
          receiptUploaded: true,
          ocrData,
          status: hasMismatch ? 'pending_review' : 'pending_review',
          verificationStatus,
          mismatchDetails: hasMismatch ? {
            nameMatch,
            amountMatch,
            refMatch,
            issues,
            kycName: currentTransaction.bankAccountTitle,
            ocrName: ocrData.senderName,
            expectedAmount,
            ocrAmount,
            expectedRef: currentTransaction.referenceNumber,
            ocrRef: ocrData.referenceNumber,
            penaltyDetails
          } : null,
          uploadedAt: new Date().toISOString(),
          // Include receipt for email attachment
          receiptBase64: receiptBase64,
          receiptName: selectedFile.name,
          receiptType: selectedFile.type,
          // Bank Transaction ID extracted from receipt
          bankTransactionId: ocrData.transactionId || null,
          // AI verification data
          aiVerificationResult: aiVerificationResult,
          aiVerificationStatus: aiVerificationResult?.overall_status || null,
          aiExtractedData: aiVerificationResult?.extracted || null,
          aiConfidence: aiVerificationResult?.confidence || null,
          aiVerifiedAt: aiVerificationResult ? new Date().toISOString() : null,
        });

        setCurrentTransaction({
          ...currentTransaction,
          receiptUploaded: true,
          ocrData,
          status: hasMismatch ? 'pending_review' : 'pending_review',
          verificationStatus,
          mismatchDetails: hasMismatch ? {
            nameMatch,
            amountMatch,
            refMatch,
            issues,
            penaltyDetails
          } : null,
          // Include receipt for email attachment
          receiptBase64: receiptBase64,
          receiptName: selectedFile.name,
          receiptType: selectedFile.type,
          // Bank Transaction ID extracted from receipt
          bankTransactionId: ocrData.transactionId || null,
          // AI verification data
          aiVerificationResult: aiVerificationResult,
          aiVerificationStatus: aiVerificationResult?.overall_status || null,
          aiExtractedData: aiVerificationResult?.extracted || null,
          aiConfidence: aiVerificationResult?.confidence || null,
        });

        if (hasMismatch) {
          setMismatchInfo({
            nameMatch,
            amountMatch,
            refMatch,
            issues,
            ocrData,
            penaltyDetails
          });
          setUploadState('mismatch');

          // Send mismatch notification via WhatsApp
          whatsappAPI.sendPaymentConfirmation(
            currentTransaction.userPhone,
            currentTransaction.id,
            currentTransaction.amount,
            'mismatch'
          );

          // Send email notification directly (no OTP needed for receipt)
          if (currentTransaction.userEmail) {
            documentNotificationService.sendReceiptNotification({
              email: currentTransaction.userEmail,
              userName: currentTransaction.userName || currentTransaction.bankAccountTitle,
              transactionId: currentTransaction.id,
              referenceNumber: currentTransaction.referenceNumber,
              amount: currentTransaction.amount,
              bankName: currentTransaction.bankName,
              accountTitle: currentTransaction.bankAccountTitle,
              status: 'mismatch',
              ocrResult: ocrData,
              mismatchDetails: { nameMatch, amountMatch, refMatch, issues, penaltyDetails },
              documentBase64: receiptBase64,
              documentName: selectedFile.name,
              documentType: selectedFile.type,
            });
          }

          // Navigate directly to transaction status page
          setTimeout(() => {
            navigate(`/status/${currentTransaction.id}`);
          }, 3000);
        } else {
          setUploadState('success');

          // Send success notification via WhatsApp
          whatsappAPI.sendPaymentConfirmation(
            currentTransaction.userPhone,
            currentTransaction.id,
            currentTransaction.amount,
            'success'
          );

          // Send email notification directly (no OTP needed for receipt)
          if (currentTransaction.userEmail) {
            documentNotificationService.sendReceiptNotification({
              email: currentTransaction.userEmail,
              userName: currentTransaction.userName || currentTransaction.bankAccountTitle,
              transactionId: currentTransaction.id,
              referenceNumber: currentTransaction.referenceNumber,
              amount: currentTransaction.amount,
              bankName: currentTransaction.bankName,
              accountTitle: currentTransaction.bankAccountTitle,
              status: 'verified',
              ocrResult: ocrData,
              documentBase64: receiptBase64,
              documentName: selectedFile.name,
              documentType: selectedFile.type,
            });
          }

          // Navigate directly to transaction status page
          setTimeout(() => {
            navigate(`/status/${currentTransaction.id}`);
          }, 3000);
        }
      } else {
        throw new Error(result.error || 'OCR processing failed');
      }
    } catch (err) {
      console.error('OCR Error:', err);
      setError('Failed to process receipt. Please try again with a clearer image.');
      setUploadState('error');
    }
  };

  if (!currentTransaction || !currentTransaction.referenceNumber) {
    return null;
  }

  return (
    <div className="upload-container animate-fade-in">
      <div className="upload-main-card">
        {/* Header Section */}
        <PageHeader showBackButton={false} greeting="Upload your receipt" />

        {/* Content Section */}
        <div className="upload-content">
          {/* Transaction Summary */}
          <div className="summary-section">
            <div className="summary-item">
              <span className="summary-label">Reference</span>
              <span className="summary-value">{currentTransaction.referenceNumber}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Amount</span>
              <span className="summary-value highlight">PKR {(currentTransaction.amount || 0).toLocaleString()}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">From Account</span>
              <span className="summary-value">{currentTransaction.bankAccountTitle}</span>
            </div>
          </div>

          {/* Upload Area */}
          <div className="section-header">
            <Upload size={16} />
            <span>Upload Receipt Screenshot</span>
          </div>

          {uploadState === 'idle' && !preview && (
            <div
              className="upload-area"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden-input"
              />
              <div className="upload-icon">
                <Upload size={40} />
              </div>
              <h3>Click or drag to upload</h3>
              <p>PNG, JPG up to 10MB</p>
              <div className="upload-options">
                <Button variant="outline" icon={Camera} size="small">
                  Take Photo
                </Button>
                <Button variant="outline" icon={Image} size="small">
                  Choose File
                </Button>
              </div>
            </div>
          )}

          {preview && uploadState === 'idle' && (
            <div className="preview-area">
              <div className="preview-image-container">
                <img src={preview} alt="Receipt preview" className="preview-image" />
                <button className="remove-btn" onClick={clearFile}>
                  <X size={20} />
                </button>
              </div>
              <div className="file-info">
                <FileText size={20} />
                <span>{selectedFile?.name}</span>
                <span className="file-size">
                  {(selectedFile?.size / 1024).toFixed(1)} KB
                </span>
              </div>
              <Button
                variant="primary"
                size="large"
                fullWidth
                onClick={handleUpload}
              >
                Verify & Submit
              </Button>
            </div>
          )}

          {(uploadState === 'uploading' || uploadState === 'processing') && (
            <div className="processing-state">
              <div className="processing-icon">
                <Loader2 className="spinner" size={48} />
              </div>
              <h3>
                {uploadState === 'uploading' ? 'Uploading Receipt...' : 'Scanning Receipt with OCR...'}
              </h3>
              <p>
                {uploadState === 'uploading'
                  ? 'Please wait while we upload your receipt'
                  : `Extracting text from image... ${ocrProgress}%`}
              </p>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: uploadState === 'uploading' ? '20%' : `${ocrProgress}%` }}
                />
              </div>
            </div>
          )}

          {uploadState === 'success' && ocrResult && (
            <div className="success-state">
              <div className="success-icon">
                <CheckCircle size={64} />
              </div>
              <h3>Receipt Verified Successfully!</h3>
              <div className="ocr-results">
                <div className="ocr-item">
                  <span className="label">Sender Name</span>
                  <span className="value">{ocrResult.senderName}</span>
                </div>
                <div className="ocr-item">
                  <span className="label">Amount</span>
                  <span className="value">
                    {ocrResult.amount ? `PKR ${ocrResult.amount.toLocaleString()}` : 'Not detected'}
                  </span>
                </div>
                {ocrResult.transactionId && (
                  <div className="ocr-item">
                    <span className="label">Bank Transaction ID</span>
                    <span className="value">{ocrResult.transactionId}</span>
                  </div>
                )}
                <div className="ocr-item">
                  <span className="label">Reference Number</span>
                  <span className="value">{ocrResult.referenceNumber || 'Not detected'}</span>
                </div>
              </div>
              <p className="redirect-message">Redirecting to status page...</p>
              {attachmentWarning && (
                <div className="attachment-warning">
                  <Info size={16} />
                  <span>Note: Receipt image could not be attached to email notification. The receipt is saved in admin panel.</span>
                </div>
              )}
            </div>
          )}

          {uploadState === 'manual_review' && (
            <div className="manual-review-state">
              <div className="manual-review-icon">
                <AlertTriangle size={64} />
              </div>
              <h3>Escalated to Manual Review</h3>
              <p>We couldn't automatically verify your receipt. Our team will review it manually.</p>
              <div className="review-info">
                <Info size={18} />
                <div>
                  <p>You will be notified once the review is complete.</p>
                  <p>This usually takes 1-2 business hours.</p>
                </div>
              </div>
              <Button
                variant="primary"
                fullWidth
                onClick={() => navigate(`/status/${currentTransaction.id}`)}
              >
                View Transaction Status
              </Button>
            </div>
          )}

          {uploadState === 'mismatch' && mismatchInfo && (
            <div className="mismatch-state">
              <div className="mismatch-icon">
                <AlertTriangle size={64} />
              </div>
              <h3>Payment Details Mismatch</h3>
              <p className="mismatch-subtitle">Your payment details do not match the expected values.</p>

              {/* Show issues */}
              <div className="mismatch-issues">
                {mismatchInfo.issues.map((issue, index) => (
                  <div key={index} className="issue-item">
                    <AlertTriangle size={18} />
                    <span>{issue.message}</span>
                  </div>
                ))}
              </div>

              {/* Penalty Warning */}
              {mismatchInfo.penaltyDetails && (
                <div className="penalty-warning">
                  <h4>Penalty Applied</h4>
                  <div className="penalty-details">
                    <div className="penalty-row">
                      <span>Original Amount</span>
                      <span>PKR {mismatchInfo.penaltyDetails.originalAmount.toLocaleString()}</span>
                    </div>
                    <div className="penalty-row penalty">
                      <span>Penalty ({mismatchInfo.penaltyDetails.penaltyRate}%)</span>
                      <span>- PKR {mismatchInfo.penaltyDetails.penaltyAmount.toLocaleString()}</span>
                    </div>
                    <div className="penalty-row return">
                      <span>Amount Returned</span>
                      <span>PKR {mismatchInfo.penaltyDetails.returnAmount.toLocaleString()}</span>
                    </div>
                  </div>
                  <p className="penalty-note">Funds will be returned to your account after deducting the penalty.</p>
                </div>
              )}

              <Button
                variant="primary"
                size="large"
                fullWidth
                icon={MessageSquare}
                className="whatsapp-btn"
                onClick={() => {
                  const message = `Hello Team AnbPay,

My payment details do not match. Please review my case.

*Transaction Details:*
- Reference No: ${currentTransaction.referenceNumber}
- Transaction ID: ${currentTransaction.id}
- KYC Name: ${currentTransaction.bankAccountTitle}
- Expected Amount: PKR ${(currentTransaction.amount || 0).toLocaleString()}

*Receipt Details:*
- Sender Name: ${mismatchInfo.ocrData?.senderName || 'N/A'}
- Receipt Amount: PKR ${(mismatchInfo.ocrData?.amount || 0).toLocaleString()}
- Reference Found: ${mismatchInfo.ocrData?.referenceNumber || 'N/A'}

Please assist me.

Thank you!`;
                  const encodedMessage = encodeURIComponent(message);
                  window.open(`https://wa.me/971561110052?text=${encodedMessage}`, '_blank');
                }}
              >
                Contact Support on WhatsApp
              </Button>

              <Button
                variant="outline"
                fullWidth
                onClick={() => navigate(`/status/${currentTransaction.id}`)}
                style={{ marginTop: '12px' }}
              >
                View Transaction Status
              </Button>
              {attachmentWarning && (
                <div className="attachment-warning" style={{ marginTop: '12px' }}>
                  <Info size={16} />
                  <span>Note: Receipt image could not be attached to email. The receipt is saved in admin panel.</span>
                </div>
              )}
            </div>
          )}

          {uploadState === 'error' && (
            <div className="error-state">
              <div className="error-icon">
                <AlertTriangle size={64} />
              </div>
              <h3>Processing Failed</h3>
              <p>{error}</p>
              <Button
                variant="primary"
                onClick={() => { setUploadState('idle'); clearFile(); }}
              >
                Try Again
              </Button>
            </div>
          )}

          {error && uploadState === 'idle' && (
            <div className="error-message">
              <AlertTriangle size={18} />
              <span>{error}</span>
            </div>
          )}

          {/* Tips Section */}
          {uploadState === 'idle' && (
            <div className="tips-section">
              <h4>Tips for Clear Receipt</h4>
              <ul>
                <li>Ensure the receipt shows sender name clearly</li>
                <li>Amount should be visible</li>
                <li>Reference number in remarks should be readable</li>
                <li>Avoid blurry or cropped images</li>
              </ul>
            </div>
          )}
        </div>

        {/* Footer Section */}
        <div className="upload-footer">
          <div className="footer-warning">
            <Shield size={16} />
            <span>Submitting fraudulent receipts will result in rejection with penalty deductions</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UploadReceipt;
